import type { MouseEventHandler } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import type {
  FontValue,
  LinkValue,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import { openWindowByLink } from '@elfsight/widget-utils';
import { useBanner } from '../../bannerProvider';
import type { BlockProps } from '../_block';
import { Block } from '../_block';

export type LinkBlockProps = BlockProps & {
  text: string;
  link?: LinkValue;
  color: string;
  fontFamily: FontValue;
  font: TextStyleValue;
  mobileFont: TextStyleValue;
  applyMobileSettings: boolean;
  customClickHandler?: MouseEventHandler<Element>;
};

export const LinkBlock = forwardRef<HTMLDivElement, LinkBlockProps>(
  (
    {
      text,
      link,
      color,
      fontFamily,
      font,
      mobileFont,
      applyMobileSettings,
      customClickHandler,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const currentFont = applyMobileSettings ? mobileFont : font;

    const rel = link?.type === 'url' ? link.rel : undefined;

    const { trackGoogleAnalyticsEvents } = useBanner();

    const clickHandler: MouseEventHandler<HTMLAnchorElement> = (e) => {
      if (trackGoogleAnalyticsEvents) {
        const valueMapping = {
          linkText: text,
          linkUrl: link?.value
        };

        trackGoogleAnalyticsEvents({
          eventType: 'linkClicked',
          valueMapping
        });
      }

      if (customClickHandler) {
        customClickHandler(e);
        return;
      }

      e.stopPropagation();

      if (link) {
        openWindowByLink(e, link);
      }
    };

    return (
      <Block {...forwardingProps} ref={forwardingRef}>
        <Link
          tabIndex={0}
          rel={rel}
          style={currentFont}
          onClick={clickHandler}
          _color={color}
          _fontFamily={fontFamily?.value}
        >
          {text}
        </Link>
      </Block>
    );
  }
);

const Link = styled.a<{
  _color: string;
  _fontFamily: string | undefined;
}>`
  cursor: pointer;
  line-height: 1.32;
  color: ${({ _color }) => _color};
  ${({ _fontFamily }) => _fontFamily && `font-family: ${_fontFamily};`}

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;
